import { useState, useEffect } from 'react';
import { aiCategorize } from '@/lib/bankTransactionsApi';

const BATCH_SIZE = 10;
const CONCURRENCY = 5;

/**
 * Loads AI category suggestions for a list of transactions in concurrent batches.
 * Returns { suggestions, loadedCount, resetKey } — ListeningsPage should re-run
 * when `open` flips to true or `transactions` identity changes.
 */
export function useAISuggestions(open, transactions) {
  const [suggestions, setSuggestions] = useState([]);
  const [loadedCount, setLoadedCount] = useState(0);

  useEffect(() => {
    if (!(open && transactions.length > 0)) return;

    const initial = transactions.map(t => ({
      id: t.id, txn: t, category: '', subcategory: '', detail: '', loading: true,
    }));
    setSuggestions(initial);
    setLoadedCount(0);

    let cancelled = false;

    const applyBatch = (start, patch) => {
      if (cancelled) return;
      setSuggestions(prev => prev.map((s, i) => (
        i >= start && i < start + BATCH_SIZE ? { ...s, ...patch(s) } : s
      )));
      setLoadedCount(c => c + Math.min(BATCH_SIZE, transactions.length - start));
    };

    const processBatch = async (start) => {
      if (cancelled) return;
      const batch = transactions.slice(start, start + BATCH_SIZE);
      try {
        const res = await aiCategorize(batch.map(t => t.id));
        if (cancelled) return;
        const sugMap = {};
        (res.suggestions || []).forEach(s => { sugMap[s.id] = s; });
        applyBatch(start, (s) => ({
          category: sugMap[s.id]?.category || '',
          subcategory: sugMap[s.id]?.subcategory || '',
          detail: sugMap[s.id]?.detail || '',
          loading: false,
        }));
      } catch {
        applyBatch(start, () => ({ loading: false, error: true }));
      }
    };

    const runAll = async () => {
      const queue = [];
      for (let start = 0; start < transactions.length; start += BATCH_SIZE) queue.push(start);
      const workers = Array.from({ length: CONCURRENCY }, async () => {
        while (queue.length > 0 && !cancelled) {
          const start = queue.shift();
          if (start === undefined) return;
          await processBatch(start);
        }
      });
      await Promise.all(workers);
    };
    runAll();

    return () => { cancelled = true; };
  }, [open, transactions]);

  return { suggestions, setSuggestions, loadedCount };
}
